import type { MenuConfig } from 'alurkerja-ui';
import { useEffect, useState } from 'react';
import {
  HouseIcon,
  Info,
  Target,
  MapPin,
  Eye,
  Flag,
  Heart,
  TrendingUp,
  Building2,
  Activity,
  Briefcase,
  ChartNoAxesColumn,
  Award,
  Shield,
  FileText,
  GraduationCap,
} from 'lucide-react';
import { useParams } from 'react-router-dom';

import useCompanyProfileCompletionState from '@/states/completion.state';

export interface ExtendedMenuConfig extends Omit<MenuConfig, 'child'> {
  role?: string[] | string;
  child?: ExtendedMenuConfig[];
  count?: string;
  newPage?: boolean;
  type?: 'label' | 'menu';
  onClick?: () => void;
  sectionKey?: string;
  percentage?: number;
}

export const useGetMenu = () => {
  const [currentMenu, setCurrentMenu] = useState<ExtendedMenuConfig[]>([]);
  const { tenant_id, organizationId } = useParams();

  const {
    sections,
    overallCompletion,
    fetchCompletion,
  } = useCompanyProfileCompletionState();

  useEffect(() => {
    fetchCompletion(
      organizationId ? Number(organizationId) : undefined
    );
  }, [tenant_id, organizationId]);

  const orgId = organizationId ?? '';
  const baseMenu: ExtendedMenuConfig[] = [
    {
      href: `/${tenant_id}/${orgId}`,
      label: 'COMPANY PROFILE',
      icon: <HouseIcon size={16} />,
      type: 'label',
    },
    {
      href: `/${tenant_id}/${orgId}`,
      label: 'Company Information',
      icon: <Info size={16} />,
      sectionKey: 'company_information',
    },
    {
      href: `/${tenant_id}/purpose`,
      label: 'Purpose',
      icon: <Target size={16} />,
      sectionKey: 'purpose',
    },
    {
      href: `/${tenant_id}/positioning`,
      label: 'Positioning',
      icon: <MapPin size={16} />,
      sectionKey: 'positioning',
    },
    {
      href: `/${tenant_id}/vision`,
      label: 'Vision',
      icon: <Eye size={16} />,
      sectionKey: 'vision',
    },
    {
      href: `/${tenant_id}/mission`,
      label: 'Mission',
      icon: <Flag size={16} />,
      sectionKey: 'mission',
    },
    {
      href: `/${tenant_id}/culture`,
      label: 'Culture',
      icon: <Heart size={16} />,
      sectionKey: 'culture',
    },
    {
      href: `/${tenant_id}/business-goal`,
      label: 'Business Goal',
      icon: <TrendingUp size={16} />,
      sectionKey: 'business_goal',
    },
    {
      href: `/${tenant_id}/organization-structure`,
      label: 'Organization Structure',
      icon: <Building2 size={16} />,
      sectionKey: 'organization',
    },
    {
      href: `/${tenant_id}/key-activities`,
      label: 'Key Activities',
      icon: <Activity size={16} />,
      sectionKey: 'key_activities',
    },
    {
      href: `/${tenant_id}/job-description`,
      label: 'Job Description',
      icon: <Briefcase size={16} />,
      sectionKey: 'job_description',
    },
    {
      href: `/${tenant_id}/report`,
      label: 'Report',
      icon: <TrendingUp size={16} />,
      sectionKey: 'report',
    },
    {
      href: `/${tenant_id}/kpi`,
      label: 'KPI',
      icon: <ChartNoAxesColumn size={16} />,
      sectionKey: 'kpi',
    },
    {
      href: `/${tenant_id}/reward`,
      label: 'Reward',
      icon: <Award size={16} />,
      sectionKey: 'reward',
    },
    {
      href: `/${tenant_id}/punishment`,
      label: 'Punishment',
      icon: <Shield size={16} />,
      sectionKey: 'punishment',
    },
    {
      href: `/${tenant_id}/sop`,
      label: 'SOP',
      icon: <FileText size={16} />,
      sectionKey: 'sop',
    },
    {
      href: `/${tenant_id}/train`,
      label: 'Train The Trainer',
      icon: <GraduationCap size={16} />,
      sectionKey: 'train_the_trainer',
    },
  ];

  useEffect(() => {
    const menuWithPercentage = baseMenu.map(menu => {
      if (menu.type === 'label') {
        return {
          ...menu,
          percentage: overallCompletion,
        };
      }

      return {
        ...menu,
        percentage: menu.sectionKey
          ? sections?.[menu.sectionKey] ?? 0
          : 0,
      };
    });

    setCurrentMenu(menuWithPercentage);
  }, [sections, overallCompletion, tenant_id, organizationId]);

  return {
    filteredMenu: currentMenu,
  };
};
